import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Users2, AlertTriangle, Loader2 } from 'lucide-react';

export const AddToTeamDialog = ({
  open, onOpenChange, registration, teams, selectedTeam, setSelectedTeam,
  warnings, requiresOverride, loading, onAdd, getTeamsForRegistration
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" data-testid="add-to-team-dialog" aria-describedby="add-to-team-description">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Users2 className="h-5 w-5 text-blue-600" />Add Player to Team</DialogTitle>
        <DialogDescription id="add-to-team-description">Select a team to add this player. You can override age/category warnings if needed.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        {registration && (
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-semibold">{registration.player_name?.charAt(0)}</span>
              </div>
              <div>
                <p className="font-medium">{registration.player_name}</p>
                <p className="text-sm text-slate-500">Registered for: <span className="font-medium uppercase">{registration.age_category}</span></p>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-2">
          <Label>Select Team</Label>
          <Select value={selectedTeam} onValueChange={(value) => { setSelectedTeam(value); }}>
            <SelectTrigger data-testid="add-to-team-select"><SelectValue placeholder="Choose a team..." /></SelectTrigger>
            <SelectContent>
              {teams.length === 0 ? (<SelectItem value="none" disabled>No teams created yet</SelectItem>) : (
                <>
                  {getTeamsForRegistration(registration).filter(t => t.isMatchingCategory).map(team => (
                    <SelectItem key={team.id} value={team.id}>
                      <div className="flex items-center gap-2">
                        <span>{team.name}</span>
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700">{team.age_category?.toUpperCase()}</Badge>
                        <span className="text-xs text-slate-400">({team.player_ids?.length || 0} players)</span>
                      </div>
                    </SelectItem>
                  ))}
                  {getTeamsForRegistration(registration).filter(t => !t.isMatchingCategory).length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs text-slate-500 border-t mt-1">Other Categories (may show warnings)</div>
                      {getTeamsForRegistration(registration).filter(t => !t.isMatchingCategory).map(team => (
                        <SelectItem key={team.id} value={team.id}>
                          <div className="flex items-center gap-2">
                            <span>{team.name}</span>
                            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">{team.age_category?.toUpperCase()}</Badge>
                            <span className="text-xs text-slate-400">({team.player_ids?.length || 0} players)</span>
                          </div>
                        </SelectItem>
                      ))}
                    </>
                  )}
                </>
              )}
            </SelectContent>
          </Select>
        </div>
        {warnings.length > 0 && (
          <div className="space-y-2">
            {warnings.map((warning, idx) => (
              <div key={idx} className={`p-3 rounded-lg border flex items-start gap-2 ${warning.severity === 'error' ? 'bg-red-50 border-red-200' : warning.severity === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                <AlertTriangle className={`h-5 w-5 mt-0.5 ${warning.severity === 'error' ? 'text-red-500' : warning.severity === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} />
                <div>
                  <p className={`text-sm font-medium ${warning.severity === 'error' ? 'text-red-700' : warning.severity === 'warning' ? 'text-amber-700' : 'text-blue-700'}`}>
                    {warning.type === 'age_ineligible' && 'Age Eligibility Warning'}
                    {warning.type === 'category_mismatch' && 'Category Mismatch'}
                    {warning.type === 'already_on_team_in_category' && 'Already on Team'}
                    {warning.type === 'registration_override' && 'Note'}
                  </p>
                  <p className={`text-sm ${warning.severity === 'error' ? 'text-red-600' : warning.severity === 'warning' ? 'text-amber-600' : 'text-blue-600'}`}>{warning.message}</p>
                </div>
              </div>
            ))}
            {requiresOverride && <p className="text-sm text-slate-600 mt-2">Click "Add Anyway" to proceed despite these warnings.</p>}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
        {requiresOverride ? (
          <Button onClick={() => onAdd(true)} disabled={loading || !selectedTeam} className="bg-amber-600 hover:bg-amber-700" data-testid="add-to-team-override-btn">
            {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</>) : (<><AlertTriangle className="h-4 w-4 mr-2" />Add Anyway</>)}
          </Button>
        ) : (
          <Button onClick={() => onAdd(false)} disabled={loading || !selectedTeam} data-testid="add-to-team-btn">
            {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Checking...</>) : 'Add to Team'}
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
